import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Eye, ChevronDown, Plus, X } from 'lucide-react';
import { useState } from 'react';
import type { OrderState } from '../../pages/CreateSalesOrderPage';
import { formatMoney } from '@/shared/utils/currency';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
}

// Very basic number to words converter for demonstration
function numberToWords(num: number): string {
  if (num === 0) return 'zero rupees only';
  if (num === 1) return 'one rupee only';
  // Simplified for prototype, would use a full library in prod
  return `${Math.floor(num)} rupees only`;
}

export function OrderTotalsSummary({ state, updateState }: Props) {
  const [showDiscount, setShowDiscount] = useState(false);
  const [showCharge, setShowCharge] = useState(false);

  const totalAmount = state.items.reduce((sum, item) => sum + item.amount, 0);
  const totalCGST = state.items.reduce((sum, item) => sum + item.cgst, 0);
  const totalSGST = state.items.reduce((sum, item) => sum + item.sgst, 0);
  const totalGST = totalCGST + totalSGST;
  const grandTotal = totalAmount + totalGST - state.discounts + state.additionalCharges;
  const currencySymbol = state.currency === 'INR' ? '₹' : '$';

  const addCustomField = () => {
    updateState(s => ({
      ...s,
      customFields: [...s.customFields, { id: Math.random().toString(), name: '', value: '' }]
    }));
  };

  const updateCustomField = (index: number, key: 'name' | 'value', val: string) => {
    updateState(s => {
      const newFields = [...s.customFields];
      newFields[index] = { ...newFields[index], [key]: val };
      return { ...s, customFields: newFields };
    });
  };

  const removeCustomField = (index: number) => {
    updateState(s => {
      const newFields = [...s.customFields];
      newFields.splice(index, 1);
      return { ...s, customFields: newFields };
    });
  };

  return (
    <div className="space-y-4">
      <Card className="border-border bg-card p-4 shadow-xs">
        <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
          <h3 className="text-label-sm uppercase tracking-wider text-on-surface-variant">Printed total</h3>
          <Eye className="h-4 w-4 text-on-surface-subtle" aria-hidden="true" />
        </div>

        <dl className="mb-4 space-y-1.5 text-data-md">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-label-sm uppercase tracking-wider text-on-surface-variant">Amount</dt>
            <dd className="font-mono tabular-nums text-right text-on-surface">{formatMoney(totalAmount, state.currency)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="flex items-center gap-1 text-label-sm uppercase tracking-wider text-on-surface-variant">
              <span className="cursor-pointer hover:text-primary">GST <EditIcon /></span>
            </dt>
            <dd className="flex items-center gap-2">
              <span className="font-mono tabular-nums text-right text-on-surface">{formatMoney(totalGST, state.currency)}</span>
              <span className="cursor-pointer text-on-surface-subtle" aria-hidden="true">×</span>
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 pl-4">
            <dt className="text-body-sm text-on-surface-variant">SGST</dt>
            <dd className="font-mono tabular-nums text-right text-body-sm text-on-surface-variant">{formatMoney(totalSGST, state.currency)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 pl-4">
            <dt className="text-body-sm text-on-surface-variant">CGST</dt>
            <dd className="font-mono tabular-nums text-right text-body-sm text-on-surface-variant">{formatMoney(totalCGST, state.currency)}</dd>
          </div>
        </dl>

        <div className="space-y-3 border-t border-border pt-3">
          {(showDiscount || state.discounts > 0) ? (
            <div className="flex items-center justify-between gap-4">
              <span className="text-body-sm text-on-surface-variant">Discount ({currencySymbol})</span>
              <Input
                type="number"
                aria-label="Discount"
                className="w-24 text-right font-mono tabular-nums"
                value={state.discounts}
                onChange={(e) => updateState(s => ({ ...s, discounts: Number(e.target.value) }))}
              />
            </div>
          ) : (
            <button onClick={() => setShowDiscount(true)} className="flex items-center gap-1 text-body-sm text-primary font-medium hover:underline">
              <Plus className="w-3 h-3" /> Add Discounts <ChevronDown className="w-3 h-3" />
            </button>
          )}

          {(showCharge || state.additionalCharges > 0) ? (
            <div className="flex items-center justify-between gap-4">
              <span className="text-body-sm text-on-surface-variant">Additional Charges ({currencySymbol})</span>
              <Input
                type="number"
                aria-label="Additional charges"
                className="w-24 text-right font-mono tabular-nums"
                value={state.additionalCharges}
                onChange={(e) => updateState(s => ({ ...s, additionalCharges: Number(e.target.value) }))}
              />
            </div>
          ) : (
            <button onClick={() => setShowCharge(true)} className="flex items-center gap-1 text-body-sm text-primary font-medium hover:underline">
              <Plus className="w-3 h-3" /> Add Additional Charges <ChevronDown className="w-3 h-3" />
            </button>
          )}
        </div>
      </Card>

      <Card className="border-border bg-card p-4 shadow-xs">
        <div className="flex items-baseline justify-between gap-3 border-t border-border pt-1">
          <span className="text-label-caps uppercase tracking-wider text-on-surface-variant">
            Total <span className="text-body-xs font-normal">({state.currency})</span>
          </span>
          <span className="font-mono tabular-nums text-right text-data-lg font-semibold text-on-surface">{formatMoney(grandTotal, state.currency)}</span>
        </div>

        {state.customFields.length > 0 && (
          <div className="mt-4 space-y-3 border-t border-border pt-3">
            {state.customFields.map((field, idx) => (
              <div key={field.id} className="flex items-center gap-2">
                <Input placeholder="Field Name" aria-label="Custom field name" className="flex-1" value={field.name} onChange={(e) => updateCustomField(idx, 'name', e.target.value)} />
                <Input placeholder="Value" aria-label="Custom field value" className="flex-1" value={field.value} onChange={(e) => updateCustomField(idx, 'value', e.target.value)} />
                <button onClick={() => removeCustomField(idx)} aria-label="Remove custom field" className="text-on-surface-subtle transition-colors hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}

        <button onClick={addCustomField} className="mt-3 flex items-center gap-1 text-body-sm font-medium text-primary hover:underline">
          <span className="text-lg leading-none">+</span> Add Custom Fields
        </button>
      </Card>

      <Card className="border-border bg-card p-4 shadow-xs">
        <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
          <h3 className="text-label-sm uppercase tracking-wider text-on-surface-variant">Printed total in words</h3>
          <Eye className="h-4 w-4 text-on-surface-subtle" aria-hidden="true" />
        </div>
        <p className="text-body-md capitalize font-medium text-on-surface">{numberToWords(grandTotal)}</p>
      </Card>
    </div>
  );
}

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
    </svg>
  );
}
